/**
 * Единый каталог продуктовых событий и пороги метрик (BLUEPRINT 1.4, Stage 13 задача 1).
 * Имена событий живут здесь, а не в API: их пишет и сервер (`trackEvent`), и клиент
 * (`analytics.track`), а панель метрик читает те же имена. Только данные — запросы в
 * `apps/api/src/modules/metrics`, тексты — в `texts.ts`.
 */

export const EVENT = {
  // ───── онбординг и пул ─────
  onboardingStarted: 'onboarding_started',
  onboardingCompleted: 'onboarding_completed',
  onboardingUpdated: 'onboarding_updated',
  poolJoined: 'pool_joined',
  poolWithdrawn: 'pool_withdrawn',
  activityProposed: 'activity_proposed',
  profileUpdated: 'profile_updated',
  referralAttached: 'referral_attached',
  botStart: 'bot_start',
  appOpened: 'app_opened',

  // ───── отряд и состав ─────
  squadFormed: 'squad_formed',
  memberJoined: 'member_joined',
  memberLeft: 'member_left',
  squadReported: 'squad_reported',
  notifyPrefsUpdated: 'notify_prefs_updated',

  // ───── сбор ─────
  rsvpSet: 'rsvp_set',
  checkinSet: 'checkin_set',
  gatheringCompleted: 'gathering_completed',
  feedbackSubmitted: 'feedback_submitted',
  reliabilityRecalc: 'reliability_recalc',
  noShow: 'no_show',
  levelAdjusted: 'level_adjusted',
  flagResolved: 'flag_resolved',

  // ───── деньги ─────
  orderCreated: 'order_created',
  orderPaid: 'order_paid',
  orderRefunded: 'order_refunded',
  orderCancelled: 'order_cancelled',
  seasonLaunched: 'season_launched',
  seasonFailed: 'season_failed',
  deadlineExtended: 'deadline_extended',

  // ───── места, замены, рефералы ─────
  reserveInviteCreated: 'reserve_invite_created',
  reserveInviteAccepted: 'reserve_invite_accepted',
  seatOpened: 'seat_opened',
  seatOffered: 'seat_offered',
  seatFilled: 'seat_filled',
  seatExpired: 'seat_expired',
  seatCancelled: 'seat_cancelled',
  /** «Оформить» в «Моих сборах»: цена и доступность позиций заявки (Stage 17, задача 2) */
  bookingPreviewed: 'booking_previewed',
  /** Человек выписался с оплаченной позиции заявки (Stage 17, задача 2) */
  orderItemCancelled: 'order_item_cancelled',
  /** Оплаченную позицию заявки вручить нельзя — она ушла «к возврату» (Stage 17, задача 3) */
  orderItemUnfulfilled: 'order_item_unfulfilled',
  /** Деньги пришли по закрытой заявке (бронь истекла) — «к возврату» целиком (Stage 17, задача 3) */
  orderPaidLate: 'order_paid_late',
  /** Оплаченные разовые места зачтены в заказ сезона (Stage 17, задача 3) */
  seasonCreditApplied: 'season_credit_applied',
  /** Капитан закрыл или открыл сбор для разовых мест (Stage 17, задача 4) */
  gatheringSinglesClosed: 'gathering_singles_closed',
  /** Человек сменил вкладку-фильтр в «Моих сборах» (Stage 17, задача 7; клиентское, `tab`) */
  myGatheringsFilter: 'my_gatherings_filter',
  referralBonusEarned: 'referral_bonus_earned',
  referralBonusApplied: 'referral_bonus_applied',

  // ───── сборка отрядов ─────
  matchingRun: 'matching_run',
  matchingApproved: 'matching_approved',
  poolTransferOffered: 'pool_transfer_offered',
  poolLongWait: 'pool_long_wait',

  // ───── идентичность и продление ─────
  squadPollStarted: 'squad_poll_started',
  squadPollVoted: 'squad_poll_voted',
  squadNameSuggested: 'squad_name_suggested',
  squadNamed: 'squad_named',
  squadEmblemSet: 'squad_emblem_set',
  squadPaused: 'squad_paused',
  extraGatheringCreated: 'extra_gathering_created',
  tournamentAssigned: 'tournament_assigned',
  tournamentResult: 'tournament_result',
  seasonRenewalOffered: 'season_renewal_offered',
  seasonRenewed: 'season_renewed',

  // ───── мультигород, площадки, компании (Stage 14) ─────
  cityCreated: 'city_created',
  cityOpened: 'city_opened',
  cityClosed: 'city_closed',
  venueBookingDecided: 'venue_booking_decided',
  venueSelfUpdated: 'venue_self_updated',
  corpLeadSubmitted: 'corp_lead_submitted',
  companyCreated: 'company_created',
  companyInvoiced: 'company_invoiced',
  companyPromoApplied: 'company_promo_applied',

  // ───── безопасность и надёжность (Stage 15) ─────
  accountDeleted: 'account_deleted',
  phonePurged: 'phone_purged',
  queueAlert: 'queue_alert',
} as const;

export type EventName = (typeof EVENT)[keyof typeof EVENT];

export const EVENT_NAMES: readonly EventName[] = Object.values(EVENT);

/**
 * Что разрешено присылать с клиента (`analytics.track`): только события, которых сервер
 * не видит сам. Всё остальное пишется на сервере — иначе метрику можно накрутить из вебвью.
 */
export const CLIENT_EVENTS = [
  EVENT.onboardingStarted,
  EVENT.appOpened,
  // Фильтр живёт на клиенте без запроса к серверу — иначе сервер смену вкладки не увидит
  EVENT.myGatheringsFilter,
] as const;
export type ClientEventName = (typeof CLIENT_EVENTS)[number];

/** Значение свойства события: то, что переживёт запись в `Event.props` (JSON). */
export type EventPropValue =
  | string
  | number
  | boolean
  | null
  | EventPropValue[]
  | { [key: string]: EventPropValue };

export type EventPropsExtra = Record<string, EventPropValue | undefined>;

/**
 * Обязательные свойства ключевых событий воронки и метрик (BLUEPRINT Stage 13, задача 1).
 * Остальные события пишут произвольные props: они нужны для разбора, а не для витрины.
 */
export interface EventPropsMap {
  [EVENT.onboardingStarted]: { step?: number };
  [EVENT.onboardingCompleted]: { cityId: string; primaryActivityId: string | null };
  [EVENT.poolJoined]: { activityId: string; squadSize: number };
  [EVENT.squadFormed]: { squadId: string; cityId: string; capacity: number; via: SquadFormedVia };
  [EVENT.memberJoined]: { squadId: string; isReserve: boolean };
  [EVENT.memberLeft]: { squadId: string; via: MemberLeftVia };
  [EVENT.orderCreated]: { orderId: string; kind: string; amountKopecks: number };
  [EVENT.orderPaid]: { orderId: string; kind: string; amountKopecks: number };
  [EVENT.orderRefunded]: { orderId: string; amountKopecks: number };
  [EVENT.rsvpSet]: { gatheringId: string; squadId: string; rsvp: string };
  [EVENT.checkinSet]: { gatheringId: string; squadId: string; mark: string; by: string };
  [EVENT.feedbackSubmitted]: { gatheringId: string; squadId: string };
  /** У пробного сбора места нет: `seatId` и `squadId` могут быть пустыми */
  [EVENT.seatFilled]: { seatId: string | null; squadId: string | null; source: string };
  [EVENT.bookingPreviewed]: { itemsCount: number; availableCount: number; amountKopecks: number };
  [EVENT.orderItemCancelled]: {
    orderId: string;
    parentOrderId: string;
    gatheringId: string;
    refundKopecks: number;
    by: string;
  };
  [EVENT.orderItemUnfulfilled]: {
    orderId: string;
    parentOrderId: string;
    gatheringId: string | null;
    refundKopecks: number;
    cause: string;
  };
  [EVENT.orderPaidLate]: { orderId: string; amountKopecks: number; closedReason: string | null };
  [EVENT.seasonCreditApplied]: { orderId: string; creditKopecks: number; itemOrderIds: string[] };
  [EVENT.gatheringSinglesClosed]: { gatheringId: string; squadId: string; closed: boolean };
  [EVENT.seasonRenewed]: { seasonId: string; squadId: string };
  [EVENT.cityOpened]: { cityId: string; slug: string };
  [EVENT.venueBookingDecided]: { gatheringId: string; venueId: string; decision: string };
  [EVENT.corpLeadSubmitted]: {
    companyName: string;
    contactName: string;
    contact: string;
    cityId: string | null;
    people: number;
    comment: string | null;
  };
  [EVENT.companyPromoApplied]: { companyId: string; promoCode: string };
}

/** Откуда взялся отряд: ручная сборка ops, утверждённое предложение алгоритма. */
export const SQUAD_FORMED_VIA = ['ops', 'matching'] as const;
export type SquadFormedVia = (typeof SQUAD_FORMED_VIA)[number];

/** Почему участник вышел из состава: ops убрал, отряд распущен, замена. */
export const MEMBER_LEFT_VIA = ['remove', 'disband'] as const;
export type MemberLeftVia = (typeof MEMBER_LEFT_VIA)[number];

/** Свойства события: у ключевых — обязательные поля, у остальных — свободный JSON. */
export type EventProps<N extends EventName> = N extends keyof EventPropsMap
  ? EventPropsMap[N] & EventPropsExtra
  : EventPropsExtra;

/**
 * Пять метрик BLUEPRINT 1.4 с порогами. `direction` — куда метрика должна двигаться:
 * `up` — чем больше, тем лучше; `down` — чем меньше, тем лучше (стоимость часа).
 * `target` у метрик без фиксированного порога — null, тогда цвет считается по тренду.
 */
export const METRICS = {
  survival: {
    key: 'survival',
    /** До какого по счёту сбора должен дожить отряд */
    gatheringIndex: 4,
    target: 60,
    direction: 'up',
    unit: 'pct',
  },
  attendance: {
    key: 'attendance',
    target: 75,
    direction: 'up',
    unit: 'pct',
  },
  renewal: {
    key: 'renewal',
    target: 40,
    direction: 'up',
    unit: 'pct',
  },
  referralSeats: {
    key: 'referralSeats',
    target: null,
    direction: 'up',
    unit: 'pct',
  },
  venueCost: {
    key: 'venueCost',
    target: null,
    direction: 'down',
    unit: 'money',
  },
} as const;

export type MetricKey = keyof typeof METRICS;
export const METRIC_KEYS = Object.keys(METRICS) as MetricKey[];

/** Настройки аналитических запросов и еженедельного отчёта владельцу (задачи 2 и 4). */
export const ANALYTICS = {
  /** Период панели по умолчанию, дней */
  defaultPeriodDays: 90,
  /** Максимальный период выборки, дней — защита от запроса «за всё время» на большой базе */
  maxPeriodDays: 365,
  /** Сколько недель показывают графики */
  weeks: 12,
  /** Явка ниже этого числа людей на сборе — аномалия в отчёте владельцу */
  lowAttendanceCount: 6,
  /** Ячейка пула ждёт дольше этого числа дней — аномалия */
  staleCellDays: 14,
  /** Сколько аномалий каждого вида показывает отчёт */
  anomalyLimit: 3,
  /** ISO-день недели и час отчёта владельцу по таймзоне города */
  ownerReportWeekday: 1,
  ownerReportHour: 9,
} as const;

export type AnalyticsConstants = typeof ANALYTICS;
